import React from "react";
import {
  SwapOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  SnippetsOutlined,
  DatabaseOutlined,
  DiffOutlined,
  ClusterOutlined,
  EyeOutlined,
  FileZipOutlined,
  ToolOutlined,
  DragOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import {
  labelForCategory,
  labelForSubCategory,
} from "../../constants/reconciliationLabels";
import { SUBCATEGORY_META } from "../../helper/masterSuiteFlow";
import "../../styles/taskModal.css";

// Sub-category icon name (from SUBCATEGORY_META) → Ant Design icon element.
const SUB_ICON = {
  snippets: <SnippetsOutlined />,
  database: <DatabaseOutlined />,
  diff: <DiffOutlined />,
  cluster: <ClusterOutlined />,
  eye: <EyeOutlined />,
  filezip: <FileZipOutlined />,
  tool: <ToolOutlined />,
  drag: <DragOutlined />,
  robot: <RobotOutlined />,
};

// Category card identity (icon + colour + one-line description). Keyed by the
// internal category `value`. "ui" is included so the Master Suite step drawer
// can reuse this component later; the Create Task modal only passes compare +
// process.
const CATEGORY_META = {
  compare: {
    icon: <SwapOutlined />,
    color: "#185FA5",
    desc: "Reconcile two data sources field by field",
  },
  process: {
    icon: <ThunderboltOutlined />,
    color: "#8A6D3B",
    desc: "Automate file movement and event-driven steps",
  },
  ui: {
    icon: <RobotOutlined />,
    color: "#5B3F9E",
    desc: "Run an automated UI test suite",
  },
};

// Shared task-type configurator: category cards → sub-type cards → the
// type-specific fields (supplied by the parent via renderFields). Presentational
// only — selection is emitted through onSelectCategory / onSelectSubCategory in
// the exact value/rawLabel shape the modal already stores, so no data changes.
function TaskTypeConfig({
  categoryKey,
  subCategoryKey,
  categoryOptions = [],
  subCategoryOptions = [],
  disabledCategory = false,
  disabledSubCategory = false,
  onSelectCategory,
  onSelectSubCategory,
  renderFields,
  errors = {},
  fieldsOnly = false,
}) {
  const subMeta = (value) => SUBCATEGORY_META[value] || null;

  // fieldsOnly (mapping open) hides the pickers but KEEPS the fields at the same
  // tree position so the sub-component instance is preserved (an early return
  // with a different shape would remount it and drop the map-open state).
  return (
    <div className="tm-config">
      {/* ── Pickers (hidden — not unmounted — while mapping is open, so the
             fields below keep a stable tree position) ─────────── */}
      <div
        className="tm-pickers"
        style={fieldsOnly ? { display: "none" } : undefined}
      >
          <div className="tm-section-title">Task type *</div>
          <div className="task-type-cards">
            {categoryOptions.map((c) => {
              const meta = CATEGORY_META[c.value] || {
                icon: <AppstoreOutlined />,
                color: "#6C8A9B",
                desc: "",
              };
              const active = categoryKey === c.value;
              const locked = disabledCategory && !active;
              return (
                <button
                  key={c.value}
                  type="button"
                  className={`task-type-card${active ? " task-type-card--active" : ""}${
                    locked ? " task-type-card--locked" : ""
                  }`}
                  style={{ "--tc-color": meta.color }}
                  disabled={disabledCategory}
                  onClick={() => onSelectCategory?.(c.value, c.label)}
                >
                  <span className="task-type-chip">{meta.icon}</span>
                  <span className="task-type-body">
                    <span className="task-type-name">
                      {labelForCategory(c.label)}
                    </span>
                    <span className="task-type-desc">{meta.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {errors.category && <p className="tm-error">{errors.category}</p>}

          {categoryKey && (
            <>
              <div className="tm-section-title" style={{ marginTop: 20 }}>
                Sub-type *
              </div>
              <div className="task-subtype-cards">
                {subCategoryOptions.map((s) => {
                  const meta = subMeta(s.value);
                  const active = subCategoryKey === s.value;
                  const locked = disabledSubCategory && !active;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      className={`task-subtype-card${
                        active ? " task-subtype-card--active" : ""
                      }${locked ? " task-subtype-card--locked" : ""}`}
                      style={{ "--tc-color": meta ? meta.color : "#6C8A9B" }}
                      disabled={disabledSubCategory}
                      onClick={() => onSelectSubCategory?.(s.value, s.label)}
                    >
                      <span className="task-subtype-chip">
                        {meta ? (
                          SUB_ICON[meta.icon] || <AppstoreOutlined />
                        ) : (
                          <AppstoreOutlined />
                        )}
                      </span>
                      <span className="task-subtype-name">
                        {labelForSubCategory(s.label)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.subCategory && (
                <p className="tm-error">{errors.subCategory}</p>
              )}
            </>
          )}
      </div>

      {/* ── Type-specific fields — stable tree position across
             fieldsOnly toggles (the sub-component must not remount) ── */}
      {subCategoryKey ? (
        <div className="tm-fields" style={fieldsOnly ? { marginTop: 0 } : undefined}>
          <div
            className="tm-section-title"
            style={fieldsOnly ? { display: "none" } : undefined}
          >
            Configuration
          </div>
          <div className="task-form">{renderFields?.(subCategoryKey)}</div>
        </div>
      ) : (
        !fieldsOnly && (
          <div className="tm-empty">Choose a task type to configure it</div>
        )
      )}
    </div>
  );
}

export default TaskTypeConfig;
